import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";
import { Dispatch } from "redux";

import { BetslipSportsbookRemoveLegClick, UI__BETSLIP_SBK_REMOVE_LEG_CLICK } from "@ppb/tbd-store/actions/betslip";
import { BettingSportsbookRemoveLegAction, BETTING__SBK_REMOVE_LEG_ACTION } from "@ppb/tbd-store/actions/betting";
import {
  getBetslipGroup,
  getBetslipPlaceStatus,
  getSportsbookConfirmationCombinations,
  getSportsbookConfirmationLegs,
  getSportsbookConfirmationRunners,
} from "../../store/state/betslip-module/betslip-module-selectors";
import {
  getSportsbookBettingCombinations,
  getSportsbookBettingLegs,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getBettingResolvers } from "../../store/state/sportsbook-betting/betting-resolver";
import { createSportsbookBettingRunnerSelector } from "../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import { Icons } from "@ppb/the-wall-icons/GenericIcon/GenericIcon";

import { BetslipSportsbookConfirmationBet } from "@ppb/tbd-store";
import { getSelectionTypeIcon } from "../../helpers/selection-type";
import { buildSelection } from "../connected-sportsbook-betslip-mapper";
import { createIsConfirmStep } from "../sportsbook-betslip-confirm-mapper";
import type { BetslipLocalization, SportsbookBetslipCapabilities, SportsbookBetslipConfig } from "../../contract";

type Runner = {
  runnerURN: string;
  selectionId: number;
  name: string;
  resultType: string | null;
};

type CardProps = {
  runnerUrns: string[];
  id: string;
  isPlacing: boolean;
  selectionTypeIcon?: Icons;
  combinationIds: string[];
  legIds: string[];
  runners: Runner[];
  subtitle: string;
};
export type StateProps = CardProps | Record<string, never>;

export type ContainerProps = {
  id: string;
  combinationIds: string[];
  hasAvailabilityHints?: boolean;
  shouldFocusStakeField?: boolean;
  localization: BetslipLocalization;
  config: SportsbookBetslipConfig;
  capabilities: SportsbookBetslipCapabilities;
};

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const getSportsbookBettingRunner = createSportsbookBettingRunnerSelector();
  const getIsConfirmStep = createIsConfirmStep();

  return (
    appState: BetslipApplicationState,
    { id, combinationIds, localization, capabilities }: ContainerProps,
  ): StateProps => {
    try {
      const isConfirmStep = getIsConfirmStep(appState);
      const group = getBetslipGroup(appState);
      const resolvers = getBettingResolvers(group);
      let combinations: BetslipSportsbookConfirmationBet["combinations"];
      let legs: BetslipSportsbookConfirmationBet["legs"];
      let sportsbookRunners: BetslipSportsbookConfirmationBet["runners"];

      const metadataRunner: {
        name: string;
        subtitle: string;
        marketType: string;
        isSuperSub: boolean;
        runnerUrn: string;
        selectionId: number;
      }[] = [];

      const combinationIdsForMapping: string[] = combinationIds ?? [id];

      if (isConfirmStep) {
        combinations = getSportsbookConfirmationCombinations(appState);
        legs = getSportsbookConfirmationLegs(appState);
        sportsbookRunners = getSportsbookConfirmationRunners(appState);
      } else {
        combinations = getSportsbookBettingCombinations(appState);
        legs = getSportsbookBettingLegs(appState);
        sportsbookRunners = appState.betting.sportsbookBetting.runners;
      }

      const legIds = [
        ...new Set(combinationIdsForMapping.flatMap((combinationId) => combinations[combinationId]?.legs ?? [])),
      ];

      const bettingRunnersMetadata = resolvers.getMetadata(appState);
      const combination = combinations[id];
      const legId = combination.legs[0];
      const runnerIds = legs[legId].runners;

      runnerIds.forEach((runnerId) => {
        const metadata = bettingRunnersMetadata[runnerId];
        if (metadata && Object.keys(metadata).length > 0) {
          const sportsbookBettingRunner = getSportsbookBettingRunner(sportsbookRunners, runnerId);
          const { name, subtitle } = buildSelection(
            metadata,
            localization,
            sportsbookBettingRunner,
            combinationIds?.length,
          );

          metadataRunner.push({
            name,
            subtitle,
            marketType: metadata.marketType,
            isSuperSub: metadata.isSuperSub ?? false,
            runnerUrn: metadata.runnerUrn,
            selectionId: sportsbookBettingRunner.selectionId,
          });
        }
      });

      const runners: Runner[] = [];
      const runnerUrns: string[] = [];
      metadataRunner.forEach((metadata) => {
        runnerUrns.push(metadata.runnerUrn);
        runners.push({
          runnerURN: metadata.runnerUrn,
          selectionId: metadata.selectionId,
          name: metadata.name,
          resultType: null,
        });
      });

      const placeStatus = getBetslipPlaceStatus(appState);
      const isPlacing = placeStatus === "INPROGRESS";

      if (!metadataRunner.length) {
        return {};
      }

      const { subtitle, marketType, isSuperSub } = metadataRunner[0];

      return {
        runnerUrns,
        id: combination.id,
        combinationIds: combinationIdsForMapping,
        isPlacing,
        selectionTypeIcon: capabilities.selectionTypeIcon.enabled
          ? getSelectionTypeIcon(marketType, isSuperSub)
          : undefined,
        legIds,
        runners,
        subtitle,
      };
    } catch (e) {
      console.error(e);

      return {};
    }
  };
};

export type DispatchActions = BetslipSportsbookRemoveLegClick | BettingSportsbookRemoveLegAction;

export type DispatchProps = {
  dispatchRemoveSelectionAction: ({ legIds, runnerUrns }: { legIds: string[]; runnerUrns: string[] }) => void;
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (
  dispatch: Dispatch<DispatchActions>,
) => ({
  dispatchRemoveSelectionAction: ({ legIds, runnerUrns }) => {
    legIds.forEach((legId) => {
      runnerUrns.forEach((runnerUrn) => {
        dispatch<BetslipSportsbookRemoveLegClick>({
          type: UI__BETSLIP_SBK_REMOVE_LEG_CLICK,
          payload: { legId, runnerUrn },
        });
      });

      dispatch<BettingSportsbookRemoveLegAction>({
        type: BETTING__SBK_REMOVE_LEG_ACTION,
        payload: { legId },
      });
    });
  },
});
